import type { LegacyNodeTree, Node, NodeId, NodeTree } from "@craftjs/core";
import { useEditor } from "@craftjs/core";
import { useCallback } from "react";

export function useCloneNodeTree() {
  const { query } = useEditor();

  const cloneNodeTree = useCallback(
    (nodeTree: NodeTree) => {
      // Temporarily use the LegaceNodeTree structure
      const newNodeTree: LegacyNodeTree = { rootNodeId: null, nodes: {} };

      const cloneNodeAndReturnId = (id: NodeId, parent?: NodeId) => {
        const node = nodeTree.nodes[id] as Node;
        const data = node;
        const newNode = query
          .parseFreshNode({
            data: {
              ...data,
              type: query.node(id).getComponent(),
              nodes: [],
              linkedNodes: {},
              parent,
            },
          })
          .toNode();

        newNode.data.nodes = data.nodes.map((childNodeId) =>
          cloneNodeAndReturnId(childNodeId, newNode.id)
        );

        newNode.data.linkedNodes = Object.entries(data.linkedNodes).reduce(
          (accum, [linkedId, linkedNodeId]) => ({
            ...accum,
            [linkedId]: cloneNodeAndReturnId(linkedNodeId, newNode.id),
          }),
          {}
        );

        newNodeTree.nodes[newNode.id] = newNode;
        return newNode.id;
      };

      const node = nodeTree.nodes[nodeTree.rootNodeId] as Node;

      newNodeTree.rootNodeId = cloneNodeAndReturnId(
        nodeTree.rootNodeId,
        node.parent
      );
      return newNodeTree;
    },
    [query]
  );

  return cloneNodeTree;
}

export function useDuplicateNode() {
  const { actions, query } = useEditor();

  const cloneNodeTree = useCloneNodeTree();

  const duplicate = useCallback(
    (nodeId: NodeId) => {
      const node = query.node(nodeId);

      // sanity check
      if (!node) {
        return;
      }

      if (query.node(nodeId).isTopLevelNode()) {
        return;
      }

      const parentNode = node.getParent();
      const indexToAdd = parentNode.indexOf(nodeId) + 1;
      const clonedNodeTree = cloneNodeTree(query.node(nodeId).toNodeTree());

      actions.addNodeTree(clonedNodeTree, parentNode.id, indexToAdd);
      actions.selectNode(clonedNodeTree.rootNodeId);

      return clonedNodeTree.rootNodeId;
    },
    [actions, query, cloneNodeTree]
  );

  return { duplicate };
}
